/**
 * Small Radix wrappers that carry no behaviour of their own. Grouped in one file
 * because each is a handful of lines and splitting them adds import noise without
 * adding clarity.
 */

import * as React from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import * as SeparatorPrimitive from '@radix-ui/react-separator';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Check, Minus } from 'lucide-react';

import { cn } from '@/lib/utils';

/* ── skeleton ───────────────────────────────────────────────────────────────── */

function Skeleton({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="skeleton"
      className={cn('bg-muted relative overflow-hidden rounded-md', className)}
      {...props}
    >
      <span className="absolute inset-0 -translate-x-full animate-[shimmer_1.6s_infinite] bg-gradient-to-r from-transparent via-foreground/[0.06] to-transparent" />
    </div>
  );
}

/* ── separator ──────────────────────────────────────────────────────────────── */

function Separator({
  className,
  orientation = 'horizontal',
  decorative = true,
  ...props
}: React.ComponentProps<typeof SeparatorPrimitive.Root>) {
  return (
    <SeparatorPrimitive.Root
      decorative={decorative}
      orientation={orientation}
      className={cn(
        'bg-border shrink-0 data-[orientation=horizontal]:h-px data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-px',
        className,
      )}
      {...props}
    />
  );
}

/* ── avatar ─────────────────────────────────────────────────────────────────── */

function Avatar({ className, ...props }: React.ComponentProps<typeof AvatarPrimitive.Root>) {
  return (
    <AvatarPrimitive.Root
      className={cn('relative flex size-8 shrink-0 overflow-hidden rounded-full', className)}
      {...props}
    />
  );
}

function AvatarImage({ className, ...props }: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return <AvatarPrimitive.Image className={cn('aspect-square size-full', className)} {...props} />;
}

/** Neutral by default — an avatar identifies, it does not signal state. */
function AvatarFallback({ className, ...props }: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      className={cn(
        'bg-foreground/[0.06] text-muted-foreground dark:bg-foreground/[0.09]',
        'flex size-full items-center justify-center rounded-full text-xs font-semibold',
        className,
      )}
      {...props}
    />
  );
}

/* ── checkbox ───────────────────────────────────────────────────────────────── */

/**
 * A 4px-radius square that picks up the brand blue only once it is actually checked —
 * selection is one of the four jobs blue is still allowed to do. At rest it is a
 * hairline; the border firms up on hover so the hit target announces itself without
 * the box ever being loud. The tick is drawn at 2.5px so it stays crisp at 16px.
 */
function Checkbox({
  className,
  indeterminate,
  ...props
}: React.ComponentProps<typeof CheckboxPrimitive.Root> & { indeterminate?: boolean }) {
  return (
    <CheckboxPrimitive.Root
      className={cn(
        'peer border-input size-4 shrink-0 rounded-[5px] border bg-transparent outline-none',
        'transition-[background-color,border-color,box-shadow] duration-150',
        'hover:border-muted-foreground/60',
        'data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-primary-foreground',
        'data-[state=indeterminate]:bg-primary data-[state=indeterminate]:border-primary data-[state=indeterminate]:text-primary-foreground',
        'focus-visible:border-ring focus-visible:ring-ring/35 focus-visible:ring-[3px]',
        'disabled:cursor-not-allowed disabled:opacity-45',
        className,
      )}
      checked={indeterminate ? 'indeterminate' : props.checked}
      {...props}
    >
      <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
        {indeterminate ? (
          <Minus className="size-3" strokeWidth={2.75} />
        ) : (
          <Check className="size-3" strokeWidth={2.75} />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
}

/* ── switch ─────────────────────────────────────────────────────────────────── */

function Switch({ className, ...props }: React.ComponentProps<typeof SwitchPrimitive.Root>) {
  return (
    <SwitchPrimitive.Root
      className={cn(
        'peer inline-flex h-5 w-9 shrink-0 items-center rounded-full border border-transparent shadow-xs transition-colors outline-none',
        'data-[state=checked]:bg-primary data-[state=unchecked]:bg-input',
        'focus-visible:ring-[3px] focus-visible:ring-ring/35 disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb className="bg-background pointer-events-none block size-4 rounded-full shadow-sm ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0.5" />
    </SwitchPrimitive.Root>
  );
}

/* ── tabs ───────────────────────────────────────────────────────────────────── */

const Tabs = TabsPrimitive.Root;

/**
 * Two tab shapes, one component.
 *
 * `pill` is a segmented control — the right thing for switching a *view* of the same
 * content inside a panel. `underline` is page-level navigation: it spans the full width,
 * its bottom hairline doubles as the rule that separates the page header from the
 * workspace below it, and the active marker is a bar rather than a raised chip.
 *
 * Every entity detail page uses `underline`, so moving between Client, Policy, Claim and
 * Company never asks the user to relearn where the navigation is or what it looks like.
 * The trigger reads its shape off the list's `data-variant`, so a call site sets it once.
 */
function TabsList({
  className,
  variant = 'pill',
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & { variant?: 'pill' | 'underline' }) {
  return (
    <TabsPrimitive.List
      data-variant={variant}
      className={cn(
        variant === 'pill'
          ? 'text-muted-foreground bg-muted inline-flex h-9 w-fit items-center justify-center rounded-lg p-[3px]'
          : 'border-border text-muted-foreground flex h-10 w-full items-center justify-start gap-1 overflow-x-auto border-b',
        className,
      )}
      {...props}
    />
  );
}

function TabsTrigger({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      className={cn(
        'inline-flex items-center justify-center gap-1.5 whitespace-nowrap outline-none',
        'text-[13px] font-medium transition-[color,box-shadow,background-color] duration-150',
        'focus-visible:ring-ring/35 focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-45',
        "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",

        // pill
        '[[data-variant=pill]_&]:h-full [[data-variant=pill]_&]:flex-1 [[data-variant=pill]_&]:rounded-md',
        '[[data-variant=pill]_&]:px-3 [[data-variant=pill]_&]:py-1',
        '[[data-variant=pill]_&]:data-[state=active]:bg-card [[data-variant=pill]_&]:data-[state=active]:text-foreground [[data-variant=pill]_&]:data-[state=active]:shadow-sm',

        // underline — the 2px marker sits *on* the list's border, not under it
        '[[data-variant=underline]_&]:relative [[data-variant=underline]_&]:h-10 [[data-variant=underline]_&]:shrink-0',
        '[[data-variant=underline]_&]:rounded-t-md [[data-variant=underline]_&]:px-3 [[data-variant=underline]_&]:text-[13.5px]',
        '[[data-variant=underline]_&]:hover:text-foreground',
        '[[data-variant=underline]_&]:data-[state=active]:text-foreground',
        '[[data-variant=underline]_&]:after:absolute [[data-variant=underline]_&]:after:inset-x-2 [[data-variant=underline]_&]:after:-bottom-px',
        '[[data-variant=underline]_&]:after:h-[2px] [[data-variant=underline]_&]:after:rounded-full',
        '[[data-variant=underline]_&]:data-[state=active]:after:bg-primary',
        className,
      )}
      {...props}
    />
  );
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return <TabsPrimitive.Content className={cn('flex-1 outline-none', className)} {...props} />;
}

/* ── tooltip ────────────────────────────────────────────────────────────────── */

const TooltipProvider = TooltipPrimitive.Provider;
const TooltipRoot = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

function TooltipContent({
  className,
  sideOffset = 6,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        sideOffset={sideOffset}
        className={cn(
          'bg-foreground text-background z-50 w-fit max-w-xs rounded-lg px-2.5 py-1.5 text-xs text-balance shadow-lg',
          'animate-tooltip animate-pop',
          className,
        )}
        {...props}
      >
        {children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

/** Convenience wrapper — the 95% case is "one trigger, one string". */
function Tooltip({
  content,
  children,
  side = 'top',
}: {
  content: React.ReactNode;
  children: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
}) {
  if (!content) return <>{children}</>;
  return (
    <TooltipRoot>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side}>{content}</TooltipContent>
    </TooltipRoot>
  );
}

/* ── popover ────────────────────────────────────────────────────────────────── */

const Popover = PopoverPrimitive.Root;
const PopoverTrigger = PopoverPrimitive.Trigger;
const PopoverAnchor = PopoverPrimitive.Anchor;

function PopoverContent({
  className,
  align = 'start',
  sideOffset = 6,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'bg-popover text-popover-foreground border-border/70 z-50 w-72 rounded-xl border p-3 shadow-lg outline-none',
          'animate-pop',
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}

/* ── progress / meter ───────────────────────────────────────────────────────── */

function Progress({
  className,
  value,
  tone = 'primary',
  ...props
}: React.ComponentProps<typeof ProgressPrimitive.Root> & {
  tone?: 'primary' | 'good' | 'warning' | 'critical';
}) {
  // The unfilled track is a lighter step of the fill's own ramp, so state reads
  // across the whole bar rather than only in the filled part.
  const fill = {
    primary: 'bg-primary',
    good: 'bg-good',
    warning: 'bg-warning',
    critical: 'bg-critical',
  }[tone];

  return (
    <ProgressPrimitive.Root
      className={cn('bg-muted relative h-1.5 w-full overflow-hidden rounded-full', className)}
      value={value}
      {...props}
    >
      <ProgressPrimitive.Indicator
        className={cn('h-full w-full flex-1 rounded-full transition-transform duration-500', fill)}
        style={{ transform: `translateX(-${100 - (value ?? 0)}%)` }}
      />
    </ProgressPrimitive.Root>
  );
}

export {
  Skeleton,
  Separator,
  Avatar,
  AvatarImage,
  AvatarFallback,
  Checkbox,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
  TooltipContent,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverAnchor,
  PopoverContent,
  Progress,
};
